import { applyFilters as applySharedFilters, type FilterConfig, type FilterState } from '@/components/filter-bar';
import type { ParkedDeposit } from '@/services/api/parked-deposits';

// Client-side filter model (the list endpoint has no filters).
// multi-text stores a single exact-match value; multi-choice stores the selected
// values — both mirror legacy BopDataTable's matching semantics (exact membership),
// now applied via the promoted shared module.
export interface ParkedDepositFilters {
  transaction_id?: string;
  currency_code?: string[];
  parked_reason?: string[];
  counterparty_iban?: string;
}

// transaction_id/counterparty_iban default to matchMode 'exact' (omitted below) — legacy
// BopDataTable multi-text is EXACT equality, not a substring search.
export const PARKED_DEPOSIT_FILTER_CONFIGS: Array<FilterConfig<ParkedDeposit>> = [
  { key: 'transaction_id', title: 'Tx Id', type: 'multi-text', getValue: (deposit) => deposit.transaction_id },
  { key: 'currency_code', title: 'Currency', type: 'multi-choice', getValue: (deposit) => deposit.currency_code },
  { key: 'parked_reason', title: 'Reason', type: 'multi-choice', getValue: (deposit) => deposit.parked_reason },
  { key: 'counterparty_iban', title: 'IBAN', type: 'multi-text', getValue: (deposit) => deposit.counterparty_iban },
];

/** ParkedDepositFilters <-> shared FilterState (Record<string, string[]>) conversion. A key is
 * absent from either shape when the column has no active filter — never present-but-empty. */
export const toSharedState = (filters: ParkedDepositFilters): FilterState => ({
  ...(filters.transaction_id ? { transaction_id: [filters.transaction_id] } : {}),
  ...(filters.currency_code && filters.currency_code.length > 0 ? { currency_code: filters.currency_code } : {}),
  ...(filters.parked_reason && filters.parked_reason.length > 0 ? { parked_reason: filters.parked_reason } : {}),
  ...(filters.counterparty_iban ? { counterparty_iban: [filters.counterparty_iban] } : {}),
});

export const fromSharedState = (state: FilterState): ParkedDepositFilters => ({
  ...(state.transaction_id?.[0] ? { transaction_id: state.transaction_id[0] } : {}),
  ...(state.currency_code && state.currency_code.length > 0 ? { currency_code: state.currency_code } : {}),
  ...(state.parked_reason && state.parked_reason.length > 0 ? { parked_reason: state.parked_reason } : {}),
  ...(state.counterparty_iban?.[0] ? { counterparty_iban: state.counterparty_iban[0] } : {}),
});

export const applyFilters = (deposits: ParkedDeposit[], filters: ParkedDepositFilters): ParkedDeposit[] =>
  applySharedFilters(deposits, PARKED_DEPOSIT_FILTER_CONFIGS, toSharedState(filters));
